import { noop } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import {
  ListingTileFilterTarget,
  ListingTileQuantityFilter,
} from 'src/contexts/MobileSortAndFilterContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { vars } from 'src/core/themes';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { useComputedState } from 'src/hooks/useComputedState';
import { ListingWithComp } from 'src/tiles/ListingTilesV2';
import { ContentId } from 'src/utils/constants/contentId';

import { InventoryEventTabs } from '../../../InventoryEventTabs';
import * as styles from './InventoryListingSortingContent.css';
import { useQuantityListingCounts } from './ListingQuantityFilterContent.hooks';

const quantitiesOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'];
const ListingQuantityFilterTargetOptions = [
  { title: ContentId.All, value: ListingTileFilterTarget.All },
  { title: ContentId.MyListings, value: ListingTileFilterTarget.MyInventory },
  {
    title: ContentId.ComparisonListings,
    value: ListingTileFilterTarget.CompInventory,
  },
];

export const ListingQuantityFilteringContent = ({
  inventory,
  activeTab,
  quantityFilter,
  onUpdateQuantityFilter,
  onClose,
}: {
  inventory: ListingWithComp[];
  activeTab: InventoryEventTabs;
  quantityFilter?: ListingTileQuantityFilter;
  onUpdateQuantityFilter: (_?: ListingTileQuantityFilter) => void;
  onClose: () => void;
}) => {
  const inferredQuantityFilter = useMemo<ListingTileQuantityFilter>(() => {
    const target =
      activeTab === InventoryEventTabs.ManualPricing
        ? ListingTileFilterTarget.All
        : ListingTileFilterTarget.MyInventory;
    if (quantityFilter) {
      return { ...quantityFilter, target };
    }
    return { target, quantities: [] };
  }, [activeTab, quantityFilter]);

  const [localQuantityFilter, setLocalQuantityFilter] = useComputedState<
    ListingTileQuantityFilter | undefined
  >(inferredQuantityFilter);

  const { targetCount, quantityCount } = useQuantityListingCounts(inventory);

  const onToggleQuantitySelect = useCallback(
    (quantityStr: string) => {
      const { quantities, target } = localQuantityFilter ?? {
        quantities: [],
        target: ListingTileFilterTarget.All,
      };
      const included = !!quantities.find((q) => q === quantityStr);
      const updated = quantities.filter((q) => q !== quantityStr);
      setLocalQuantityFilter({
        target: target,
        quantities: included ? updated : [...updated, quantityStr],
      });
    },
    [localQuantityFilter, setLocalQuantityFilter]
  );

  const onSelectQuantityFilterTarget = useCallback(
    (value: ListingTileFilterTarget) => {
      const { quantities } = localQuantityFilter ?? {};
      setLocalQuantityFilter({ quantities: quantities ?? [], target: value });
    },
    [localQuantityFilter, setLocalQuantityFilter]
  );

  const onAddQuantities = useCallback(() => {
    onUpdateQuantityFilter(localQuantityFilter);
    onClose();
  }, [localQuantityFilter, onClose, onUpdateQuantityFilter]);

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      alignItems="center"
      gap="m"
      style={{ color: vars.color.textPrimary }}
    >
      <Stack direction="column" width="full" gap="l">
        <span className={styles.sortAndFilterHeader}>
          <Content id={ContentId.FilterQuantities} />
        </span>
      </Stack>
      <Stack
        direction="column"
        width="full"
        gap="l"
        style={{ flex: 1, overflowY: 'auto' }}
      >
        <Stack direction="column" gap="m">
          <span>
            <Content id={ContentId.SelectInventory} />
          </span>
          <RadioGroup value={localQuantityFilter?.target} onValueChange={noop}>
            <Stack gap="s" alignItems="center" direction="column" width="full">
              {ListingQuantityFilterTargetOptions.map(({ title, value }) => {
                return (
                  <Stack
                    key={title}
                    role="button"
                    width="full"
                    justifyContent="spaceBetween"
                    className={styles.inventoryFilterGroupItem}
                    onClick={() => onSelectQuantityFilterTarget(value)}
                  >
                    <Radio
                      value={value}
                      label={<Content id={title} />}
                      onChange={noop}
                    />
                    <span>{targetCount[value] ?? 0}</span>
                  </Stack>
                );
              })}
            </Stack>
          </RadioGroup>
          <span>
            <Content id={ContentId.Quantities} />
          </span>
          <Stack
            direction="column"
            gap="m"
            className={styles.inventoryFilterGroupItem}
          >
            {quantitiesOptions.map((qantity) => {
              const quantityListingCount = quantityCount[qantity] ?? 0;
              return (
                <Stack
                  key={qantity}
                  role="button"
                  width="full"
                  alignItems="center"
                  justifyContent="spaceBetween"
                  onClick={() => onToggleQuantitySelect(qantity)}
                >
                  <Checkbox
                    color={vars.color.textBrand}
                    checked={(localQuantityFilter?.quantities ?? []).includes(
                      qantity
                    )}
                    onChange={noop}
                    label={qantity}
                    labelPosition="right"
                    checkboxSizeVariant="light"
                  />
                  <span>{quantityListingCount}</span>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      <Stack justifyContent="spaceBetween" width="full" alignItems="center">
        <Button
          style={{ height: '40px' }}
          shape="rect"
          variant="outlineGray"
          onClick={() =>
            setLocalQuantityFilter({
              target: ListingTileFilterTarget.All,
              quantities: [],
            })
          }
        >
          <Content id={ContentId.Clear} />
        </Button>
        <Button
          style={{ height: '40px' }}
          shape="rect"
          onClick={onAddQuantities}
        >
          <Content id={ContentId.AddQuantities} />
        </Button>
      </Stack>
    </Stack>
  );
};
